import React, { useState } from "react";
import { usePricingConfigStore } from "@/stores/usePricingConfigStore";
import { trpc } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, Download } from "lucide-react";
import { exportToExcel } from "@/lib/excel.utils";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  pricingConfigMachineSchema,
  PricingConfigMachineForm,
} from "@/lib/schemas/pricing-config";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const MachineConfigTab = () => {
  const queryClient = useQueryClient();
  const {
    isMachineModalOpen,
    selectedMachineId,
    openMachineModal,
    closeMachineModal,
  } = usePricingConfigStore();
  const { data: machinesData, isLoading } = useQuery(
    trpc.pricingConfig.getMachines.queryOptions(),
  );
  const machines = machinesData as unknown as any[];

  const deleteMutation = useMutation({
    ...trpc.pricingConfig.deleteMachine.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã xóa máy in");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getMachines.queryKey(),
      });
    },
    onError: (err: any) => {
      toast.error("Lỗi khi xóa máy in: " + err.message);
    },
  });

  const handleDelete = (id: string) => {
    if (confirm("Bạn có chắc chắn muốn xóa?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2">
        <Button
          variant="outline"
          onClick={() =>
            exportToExcel(machines || [], [
              { key: "code", label: "Mã máy", width: 14 },
              { key: "name", label: "Tên máy", width: 28 },
              { key: "maxSheetWidth", label: "Khổ rộng tối đa", width: 14 },
              { key: "maxSheetLength", label: "Khổ dài tối đa", width: 14 },
              { key: "baseBatchSize", label: "Batch size", width: 12 },
              { key: "status", label: "Trạng thái", width: 12 },
            ], "cau_hinh_may_in")
          }
        >
          <Download className="w-4 h-4 mr-2" /> Xuất Excel
        </Button>
        <Button onClick={() => openMachineModal()}>
          <Plus className="w-4 h-4 mr-2" /> Thêm máy in
        </Button>
      </div>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mã máy</TableHead>
              <TableHead>Tên máy</TableHead>
              <TableHead>Khổ tối đa</TableHead>
              <TableHead>Batch Size</TableHead>
              <TableHead>Trạng thái</TableHead>
              <TableHead className="text-right">Thao tác</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : machines?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-10 text-muted-foreground"
                >
                  Chưa có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              machines?.map((machine) => (
                <TableRow key={machine.id} className="cursor-pointer" onClick={() => openMachineModal(machine.id)}>
                  <TableCell className="font-medium">{machine.code}</TableCell>
                  <TableCell>{machine.name}</TableCell>
                  <TableCell>
                    {machine.maxSheetWidth} x {machine.maxSheetLength}
                  </TableCell>
                  <TableCell>{machine.baseBatchSize}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        machine.status === "ACTIVE" ? "default" : "secondary"
                      }
                    >
                      {machine.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(machine.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <MachineConfigModal
        isOpen={isMachineModalOpen}
        onClose={closeMachineModal}
        machineId={selectedMachineId}
      />
    </div>
  );
};

const MachineConfigModal = ({
  isOpen,
  onClose,
  machineId,
}: {
  isOpen: boolean;
  onClose: () => void;
  machineId: string | null;
}) => {
  const queryClient = useQueryClient();
  const { data: machineData, isLoading: isLoadingMachine } = useQuery({
    ...trpc.pricingConfig.getMachineById.queryOptions(machineId!),
    enabled: !!machineId,
  });
  const machine = machineData as unknown as any;

  const form = useForm<PricingConfigMachineForm>({
    resolver: zodResolver(pricingConfigMachineSchema),
    defaultValues: {
      code: "",
      name: "",
      maxSheetWidth: 0,
      maxSheetLength: 0,
      baseBatchSize: 2000,
      cmykBatchCost: 0,
      cmykRunOnCost: 0,
      spotColorCostMultiplier: 2.0,
      uvInkCostMultiplier: 3.0,
      plateCost: 0,
      makeReadyCost: 0,
      washUpCostSpotColor: 0,
      status: "ACTIVE",
    },
  });

  React.useEffect(() => {
    if (machine) {
      form.reset({
        ...machine,
        cmykBatchCost: Number(machine.cmykBatchCost) || 0,
        cmykRunOnCost: Number(machine.cmykRunOnCost) || 0,
        plateCost: Number(machine.plateCost) || 0,
        makeReadyCost: Number(machine.makeReadyCost) || 0,
        washUpCostSpotColor: Number(machine.washUpCostSpotColor) || 0,
      });
    } else {
      form.reset({
        code: "",
        name: "",
        maxSheetWidth: 0,
        maxSheetLength: 0,
        baseBatchSize: 2000,
        cmykBatchCost: 0,
        cmykRunOnCost: 0,
        spotColorCostMultiplier: 2.0,
        uvInkCostMultiplier: 3.0,
        plateCost: 0,
        makeReadyCost: 0,
        washUpCostSpotColor: 0,
        status: "ACTIVE",
      });
    }
  }, [machine, isOpen, form]);

  const createMutation = useMutation({
    ...trpc.pricingConfig.createMachine.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã thêm cấu hình máy in");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getMachines.queryKey(),
      });
      onClose();
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const updateMutation = useMutation({
    ...trpc.pricingConfig.updateMachine.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã cập nhật cấu hình máy in");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getMachines.queryKey(),
      });
      onClose();
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const onSubmit = (values: PricingConfigMachineForm) => {
    if (machineId) {
      updateMutation.mutate({ id: machineId, data: values });
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {machineId ? "Cập nhật cấu hình máy in" : "Thêm máy in mới"}
          </DialogTitle>
        </DialogHeader>

        {machineId && isLoadingMachine ? (
          <div className="flex justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mã máy</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tên máy</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxSheetWidth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Khổ rộng tối đa (cm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxSheetLength"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Khổ dài tối đa (cm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="baseBatchSize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Batch size cơ bản (VD: 2000 tờ)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cmykBatchCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Giá In CMYK cho 1 Batch</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cmykRunOnCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Giá In CMYK tờ chạy thêm (Run-on)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="spotColorCostMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hệ số giá màu pha (Spot Color)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="uvInkCostMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hệ số giá mực UV</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="plateCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Giá kẽm (1 màu)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="makeReadyCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phí chuẩn bị máy (Make Ready)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="washUpCostSpotColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phí rửa máy (Màu pha)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Hủy
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Đang lưu..."
                    : "Lưu cấu hình"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MachineConfigTab;
